import type LabeledValueView from '@UI/Store/Shared/Views/LabeledValueView';

/** Key of the section holding the variables a gabarit declares outside any group. */
export const ROOT_GROUP_KEY = 'root';

/** What a declared group needs to be placed in the tree. */
export type DeclarationNodeSource = {
	readonly uuid: string;
	readonly path: string;
	readonly name: string;
	readonly isList: boolean;
	readonly order: number;
};

/** What a declared variable needs to be placed in the tree and displayed. */
export type DeclarationFieldSource = {
	readonly uuid: string;
	readonly path: string;
	readonly label: string;
	readonly type: LabeledValueView;
	readonly isRequired: boolean;
	readonly order: number;
};

export type DeclarationEntry = DeclarationGroupView | DeclarationFieldSource;

type DeclarationGroupArgs = {
	readonly key: string;
	readonly path: string | null;
	readonly name: string | null;
	readonly isList: boolean;
	readonly order: number;
	readonly entries: readonly DeclarationEntry[];
};

type Child =
	| { readonly kind: 'node'; readonly node: DeclarationNodeSource }
	| { readonly kind: 'field'; readonly field: DeclarationFieldSource };

const resolveParentPath = (path: string): string => {
	const separatorIndex = path.lastIndexOf('.');
	return separatorIndex === -1 ? '' : path.slice(0, separatorIndex);
};

const compareByOrderThenPath = (
	left: { readonly order: number; readonly path: string },
	right: { readonly order: number; readonly path: string },
): number => {
	return left.order !== right.order ? left.order - right.order : left.path.localeCompare(right.path);
};

/**
 * One declared group and what it holds, nested. Immutable View: `readonly` properties, frozen at the
 * end of the constructor.
 *
 * `name` and `path` are `null` on the root group alone — it has no declared node of its own, so the
 * screen supplies its heading. `isList` is then `false`: only a declared node can be repeatable.
 */
class DeclarationGroupView {
	readonly key: string;
	readonly path: string | null;
	readonly name: string | null;
	readonly isList: boolean;
	readonly order: number;
	readonly entries: readonly DeclarationEntry[];

	constructor(args: DeclarationGroupArgs) {
		this.key = args.key;
		this.path = args.path;
		this.name = args.name;
		this.isList = args.isList;
		this.order = args.order;
		this.entries = args.entries;

		Object.freeze(this);
	}

	/** Only a top-level declared node can be marked for reevaluation: never the root, never a nested node. */
	get isReevaluationMarkable(): boolean {
		return this.key !== ROOT_GROUP_KEY && this.path !== null && !this.path.includes('.');
	}

	get hasFields(): boolean {
		return this.entries.some((entry) => !(entry instanceof DeclarationGroupView));
	}

	/**
	 * Builds the top-level sections of a declaration: the root group first when variables sit outside
	 * any node, then the first-level nodes in declaration order.
	 *
	 * The declaration carries no parent reference: nesting is told by dotted paths (`a.b` is a child of
	 * `a`, a field `a.b.c` a child of `a.b`). A node or field whose parent is not declared attaches to
	 * its nearest declared ancestor, else to the root, so nothing vanishes. Entries of a level are
	 * interleaved by `order`, then `path`.
	 */
	static buildSections(
		nodes: readonly DeclarationNodeSource[],
		fields: readonly DeclarationFieldSource[],
	): readonly DeclarationGroupView[] {
		const declaredPaths = new Set(nodes.map((node) => node.path));
		const childrenByParentPath = new Map<string, Child[]>();

		const attach = (path: string, child: Child): void => {
			let ancestorPath = resolveParentPath(path);
			while (ancestorPath !== '' && !declaredPaths.has(ancestorPath)) {
				ancestorPath = resolveParentPath(ancestorPath);
			}
			const siblings = childrenByParentPath.get(ancestorPath);
			if (siblings === undefined) {
				childrenByParentPath.set(ancestorPath, [child]);
			} else {
				siblings.push(child);
			}
		};

		for (const node of nodes) {
			attach(node.path, { kind: 'node', node });
		}
		for (const field of fields) {
			attach(field.path, { kind: 'field', field });
		}

		const resolveSource = (child: Child): DeclarationNodeSource | DeclarationFieldSource => {
			return child.kind === 'node' ? child.node : child.field;
		};

		const buildEntries = (parentPath: string): DeclarationEntry[] => {
			return [...(childrenByParentPath.get(parentPath) ?? [])]
				.sort((left, right) => compareByOrderThenPath(resolveSource(left), resolveSource(right)))
				.map((child) => (child.kind === 'node' ? buildGroup(child.node) : child.field));
		};

		const buildGroup = (node: DeclarationNodeSource): DeclarationGroupView => {
			return new DeclarationGroupView({
				key: node.uuid,
				path: node.path,
				name: node.name,
				isList: node.isList,
				order: node.order,
				entries: buildEntries(node.path),
			});
		};

		const rootEntries = buildEntries('');
		const rootFields = rootEntries.filter((entry) => !(entry instanceof DeclarationGroupView));
		const topLevelGroups = rootEntries.filter((entry) => entry instanceof DeclarationGroupView);

		if (rootFields.length === 0) {
			return topLevelGroups;
		}

		return [
			new DeclarationGroupView({
				key: ROOT_GROUP_KEY,
				path: null,
				name: null,
				isList: false,
				order: 0,
				entries: rootFields,
			}),
			...topLevelGroups,
		];
	}
}

export default DeclarationGroupView;
